import React from "react";
import { Typography } from "@mui/material";
import api from "../../services/api";

const fmtUsd = (val) =>
    Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * ConversionPreview
 *
 * Shows a live "≈ $X.XX USD" line under an amount field while the user
 * types a foreign-currency expense, using that day's historical rate.
 * Renders nothing for USD (no conversion to preview).
 *
 * Props:
 *   amount   - entered amount, in `currency` (string | number)
 *   currency - selected currency code (e.g. "CAD")
 *   date     - expense date, "yyyy-MM-dd" (rate is looked up as of this day)
 */
export default function ConversionPreview({ amount, currency, date }) {
    const [state, setState] = React.useState({ loading: false, usd: null, error: null });

    React.useEffect(() => {
        if (!currency || currency === "USD" || !amount || Number(amount) <= 0 || !date) {
            setState({ loading: false, usd: null, error: null });
            return;
        }

        let active = true;
        setState((s) => ({ ...s, loading: true, error: null }));

        const handle = setTimeout(async () => {
            try {
                const res = await api.get("/api/expenses/exchange-rate-preview/", {
                    params: { currency, date, amount },
                });
                if (active) setState({ loading: false, usd: res.data.usd_amount, error: null });
            } catch {
                if (active) setState({ loading: false, usd: null, error: "Couldn't get a live rate." });
            }
        }, 500);

        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [amount, currency, date]);

    if (!currency || currency === "USD") return null;
    if (state.loading) {
        return (
            <Typography variant="caption" color="text.secondary">
                Converting…
            </Typography>
        );
    }
    if (state.error) {
        return (
            <Typography variant="caption" color="error">
                {state.error}
            </Typography>
        );
    }
    if (state.usd == null) return null;

    return (
        <Typography variant="caption" color="text.secondary">
            ≈ {fmtUsd(state.usd)} USD
        </Typography>
    );
}
